import React, { useEffect, useState } from "react";
import { Container, Icon, Loader, Pagination, Table } from "semantic-ui-react";
import { formatDate } from "../../constants";
import { Badge, InfoNote, cardStyle, tdStyle, thStyle, ui } from "../../components/adminUi";
import { getSupportTicketStatusLabel } from "./supportTicketUtils";
import RedDot from "../../components/RedDot";

/**
 * The chrome shared by the four support-ticket pages.
 *
 * There are two lists (admin / mine) and two threads (admin / mine) that differ in about
 * four things each: one column, one route, one label, one button. Written out separately
 * that is four copies of a header block, a table, a card layout and an attachment — and the
 * copies drift, which is the failure this codebase has already paid for once in navLabels.
 *
 * Only genuinely shared pieces live here. Anything one page does and another does not — the
 * 미답변 count, the 요청 종료 button, which side of the thread is "sent" — stays in the page.
 */

/** Below this the list becomes cards. Matches StaffHome; a 3–4 column table with a fluid
 *  body cell does not survive 390px. */
export const NARROW = 700;

export function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= NARROW);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= NARROW);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isNarrow;
}

/** Sticky translucent header. `right` is the page's own slot — a badge, nothing, anything. */
export function PageHeader({ icon, title, subtitle, right, backTo }) {
  return (
    <div
      style={{
        padding: "16px 0",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(20px)",
        borderBottom: `1px solid ${ui.border}`,
        marginBottom: "20px",
      }}
    >
      <Container fluid>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            // Wraps: page headers in this app have shipped overflowing a phone more than
            // once from a title and its actions sharing one unwrappable row.
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <IconChip icon={icon} backTo={backTo} />
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontSize: "17px", fontWeight: 600, margin: 0, color: ui.text }}>
                {title}
              </h2>
              <p style={{ fontSize: "13px", color: ui.muted, margin: 0 }}>{subtitle}</p>
            </div>
          </div>
          {right}
        </div>
      </Container>
    </div>
  );
}

/** On a detail page the chip is the back link — the one affordance that belongs in that
 *  slot. On a list page it is decoration. */
function IconChip({ icon, backTo }) {
  const style = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: ui.chip,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };
  const glyph = (
    <Icon name={backTo ? "arrow left" : icon} style={{ margin: 0, color: ui.accent, fontSize: "17px" }} />
  );
  if (!backTo) return <div style={style}>{glyph}</div>;
  // Plain <a> via the caller's Link is not needed here; the caller passes a Link element.
  return React.cloneElement(backTo, { style, title: "목록으로" }, glyph);
}

/**
 * Attachments are whatever the reporter screenshotted, and a phone screenshot is very tall.
 * Uncapped, two of them turned a thread into a 5,000px scroll with the compose box far
 * below the fold. Capped, and click-to-open so nothing is lost.
 */
export function Attachment({ url, style }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" title="클릭하면 원본 크기로 열립니다">
      <img
        src={url}
        alt="첨부 이미지"
        style={{
          maxWidth: "100%",
          maxHeight: "360px",
          width: "auto",
          objectFit: "contain",
          borderRadius: 12,
          display: "block",
          border: `1px solid ${ui.border}`,
          ...style,
        }}
      />
    </a>
  );
}

export function ErrorNote({ children }) {
  return (
    <InfoNote style={{ background: ui.redBg, color: ui.redFg }}>
      <Icon name="exclamation triangle" style={{ margin: 0 }} />
      <span>{children}</span>
    </InfoNote>
  );
}

export function EmptyCard({ icon = "inbox", children }) {
  return (
    <div style={{ ...cardStyle, padding: "48px 24px", textAlign: "center", color: ui.muted }}>
      <Icon name={icon} size="big" style={{ color: "#d0d0d5" }} />
      <div style={{ marginTop: "10px", fontSize: "14px", lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

/**
 * The ticket list. `showAuthor` is the only structural difference between the two pages —
 * on 내 피드백 every row is yours, so the column would be one repeated name.
 */
// A reply you haven't opened yet (#271); `unread` is only ever true on your own tickets.
const NewReply = () => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 600, color: "#FF3B30", whiteSpace: "nowrap" }}>
    <RedDot /> 새 답변
  </span>
);

export function TicketList({ tickets, showAuthor, onOpen, isNarrow }) {
  if (isNarrow) {
    return (
      <div style={{ display: "grid", gap: "12px" }}>
        {tickets.map((ticket) => {
          const status = getSupportTicketStatusLabel(ticket.status);
          return (
            <div
              key={ticket.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(ticket)}
              onKeyDown={(e) => e.key === "Enter" && onOpen(ticket)}
              style={{ ...cardStyle, padding: "14px 16px", cursor: "pointer" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  marginBottom: "8px",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <Badge tone={status.tone}>{status.text}</Badge>
                  {ticket.unread && <NewReply />}
                </span>
                <span style={{ fontSize: "12px", color: ui.muted }}>
                  {formatDate(ticket.created_at)}
                </span>
              </div>
              <div style={{ fontSize: "14px", color: ui.text, lineHeight: 1.5 }}>
                {ticket.body}
              </div>
              {showAuthor && (
                <div style={{ fontSize: "12px", color: ui.muted, marginTop: "8px" }}>
                  {ticket.author?.full_name || ticket.author?.username}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <Table basic="very" style={{ margin: 0, border: "none" }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ ...thStyle, width: "110px" }}>상태</Table.HeaderCell>
            {showAuthor && (
              <Table.HeaderCell style={{ ...thStyle, width: "160px" }}>작성자</Table.HeaderCell>
            )}
            <Table.HeaderCell style={thStyle}>내용</Table.HeaderCell>
            {/* 170px + nowrap: formatDate emits "2026/08/06 05:32 PM", which wrapped the
                meridiem onto its own line at 150px. */}
            <Table.HeaderCell style={{ ...thStyle, width: "170px" }}>제출일</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {tickets.map((ticket) => {
            const status = getSupportTicketStatusLabel(ticket.status);
            return (
              <Table.Row
                key={ticket.id}
                onClick={() => onOpen(ticket)}
                style={{ cursor: "pointer" }}
              >
                <Table.Cell style={tdStyle}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    <Badge tone={status.tone}>{status.text}</Badge>
                    {ticket.unread && <NewReply />}
                  </span>
                </Table.Cell>
                {showAuthor && (
                  <Table.Cell style={tdStyle}>
                    {ticket.author?.full_name || ticket.author?.username}
                  </Table.Cell>
                )}
                {/* Ellipsis in CSS, not slice(0, 80): a fixed cut against a fluid column
                    truncated early on a wide screen and still overflowed on a narrow one. */}
                <Table.Cell
                  title={ticket.body}
                  style={{
                    ...tdStyle,
                    maxWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ticket.body}
                </Table.Cell>
                <Table.Cell style={{ ...tdStyle, color: ui.muted, whiteSpace: "nowrap" }}>
                  {formatDate(ticket.created_at)}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}

export function ListPagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
      <Pagination
        activePage={Number(page)}
        totalPages={totalPages}
        onPageChange={onChange}
        siblingRange={1}
        boundaryRange={0}
        ellipsisItem={null}
      />
    </div>
  );
}

export function PageBody({ children, maxWidth }) {
  return (
    <Container fluid style={{ padding: "0 20px 40px", ...(maxWidth ? { maxWidth } : {}) }}>
      {children}
    </Container>
  );
}

export function Page({ children }) {
  return (
    <div style={{ backgroundColor: ui.pageBg, minHeight: "calc(100vh - 50px)" }}>{children}</div>
  );
}

export function CenteredLoader() {
  return <Loader active inline="centered" style={{ marginTop: "60px" }} />;
}
